import {useFieldConfigurationContext} from '@hconnect/tuc-processes/configuration/useConfigurationContext'
import moment from 'moment'
import {useMemo} from 'react'

import {getDayCode} from '../../../../../../../Organisms/OrderIntake/utils'

import {
  useFeatureShowOutOfOperationalHoursSlotsForRmcOrderIntake,
  useFeatureSlotManagementForRmcOrderIntakeEnabled
} from './useFeatureFlag'

export type OffHoursSlotsParams = {
  shippingDate: string | undefined
}

export type OffHoursSlotsResponse = {
  error?: string
  offHoursSlots: string[]
}

export const useOffHoursSlots = ({shippingDate}: OffHoursSlotsParams): OffHoursSlotsResponse => {
  const slotManagementEnabled = useFeatureSlotManagementForRmcOrderIntakeEnabled()
  const isOffHoursSlotsForOIEnabled = useFeatureShowOutOfOperationalHoursSlotsForRmcOrderIntake()
  const dateTimeConfiguration = useFieldConfigurationContext('dateTime')

  // Generate off-hours slots based on configuration
  const offHoursSlots = useMemo((): string[] => {
    if (
      !shippingDate ||
      !slotManagementEnabled ||
      !isOffHoursSlotsForOIEnabled ||
      !dateTimeConfiguration
    ) {
      return []
    }

    const dayOfWeek = getDayCode(shippingDate)
    const businessDay = dateTimeConfiguration.businessDays.find((bd) => bd.dayOfWeek === dayOfWeek)

    const loadTimes = {
      earliest: moment(dateTimeConfiguration.businessHours?.earliestPossibleLoadTime, 'HH:mm'),
      latest: moment(dateTimeConfiguration.businessHours?.latestPossibleLoadTime, 'HH:mm')
    }
    const businessHours = {
      start: moment(businessDay?.businessHoursStart, 'HH:mm'),
      end: moment(businessDay?.businessHoursEnd, 'HH:mm')
    }

    const offHoursSlots: string[] = []
    // If no gap between earliest load time and business hours start, no off-hours
    if (businessHours.start.isBefore(loadTimes.earliest)) {
      const current = businessHours.start.clone()

      while (current.isBefore(loadTimes.earliest)) {
        offHoursSlots.push(current.format('HH:mm'))
        current.add(15, 'minutes')
      }
    }
    if (businessHours.end.isAfter(loadTimes.latest)) {
      // offset the latest load time by 15 minutes to avoid overlaping
      //  with the latest available slot since available slots are inclusive of latest load time
      const current = loadTimes.latest.clone().add(15, 'minutes')

      while (current.isBefore(businessHours.end)) {
        offHoursSlots.push(current.format('HH:mm'))
        current.add(15, 'minutes')
      }
    }

    return offHoursSlots
  }, [shippingDate, slotManagementEnabled, isOffHoursSlotsForOIEnabled, dateTimeConfiguration])

  return {offHoursSlots}
}
